import { IBranch } from './branch.model';
import { ISyncStatus } from './dataSync.model';
import { IOdataResult } from '../odata.model';
import { IJobDetail, IRepairOrderJob, JobDetailType } from './repairOrderJob.model';
import { ITypeCodeSimple } from './typecode.model';
import { getEnumKeyByEnumValue } from '../../common/util';

export interface IRepairOrder {
  repairOrderNumber?: number; // Can be null for ADHOC ROs
  registrationNumber: string;
  dropOffDateTime: string;
  pickUpDateTime: string;
  driver: IContact;
  owner: IContact;
  progressStatus: ITypeCodeSimple<string>;
  branch: string;
  isClosed: boolean;
  isQuotation: boolean;
  isConfirmed: boolean;
  isInvoiced: boolean;
  isVetted: boolean;
  vehicle: IVehicle;
  jobs: IRepairOrderJob[];
  fromDetail: boolean; // A flag indicating the RO was loaded from the detail endpoint
  isAdhoc: boolean; // A  flag indicating the RO was added as an adhoc RO
}

export interface IContact {
  code: string;
  name: string;
  companyName: string;
  address: string;
  businessPhone: string;
  mobilePhone: string;
  privatePhone: string;
}

export interface IRepairOrderWithJobsDiff {
  new: IOdataResult<IRepairOrder>;
  deleted: number[];
}

export interface IRepairOrderSyncStatus extends ISyncStatus {
  data: IRepairOrder[];
}

export interface IRepairOrderSummary {
  repairOrderNumber: number;
  registrationNumber: string;
  dropOffDateTime: string;
  pickUpDateTime: string;
  driver: IContact;
  owner: IContact;
  status: IStatus;
  progressStatus: ITypeCodeSimple<string>;
  branch: string;
  isClosed: boolean;
  isQuotation: boolean;
  isConfirmed: boolean;
  isInvoiced: boolean;
  isVetted: boolean;
  vehicle: IVehicle;
}

export interface IRepairOrderDetail {
  registrationNumber: string;
  advisor: IContact;
  branch: IBranch;
  franchise: ITypeCodeSimple<string>;
  jobs: IJobDetail[];
  loanCar: ILoanCar;
  preferredContactMethod: IPreferredContactMethod;
  repairOrderNumber: number;
  status: IStatus;
  vehicle: IVehicle;
  driver: IContact;
  owner: IContact;
  dropOffDateTime: string;
  pickUpDateTime: string;
  progressStatus: ITypeCodeSimple<string>;
}

export interface IRepairOrderDetailCode {
  code: string;
  name: string;
}

export interface ILoanCar {
  class?: string;
  id?: string;
  branchCode: string;
}

export interface IPreferredContactMethod {
  type?: string;
  value?: string;
}

export interface IStatus {
  isConfirmed: boolean;
  isVetted: boolean;
  isModified: boolean;
  isDeleteRequested: boolean;
  isDeletable: boolean;
  isQuotation: boolean;
  isClosed: boolean;
  isInvoiced: boolean;
}

export interface IVehicle {
  description: string;
  registration: string;
  source: string;
  sourceRef: string;
  stockNumber?: number;
  vin?: string;
  color?: string;
  fleetNumber?: string;
}

export interface IRepairOrderAllResponse {
  //roSearch?: IRepairOrder;
  validRos?: IOdataResult<IRepairOrder>;
  invalidRos?: IOdataResult<IRepairOrder>;
  deletedRos?: number[];
}

export function mapRepairOrderDetailToRepairOrderWithJobs(input: IRepairOrderDetail) {
  if (input) {
    return {
      branch: input.branch.code,
      driver: input.driver,
      dropOffDateTime: input.dropOffDateTime,
      isClosed: input.status.isClosed,
      isConfirmed: input.status.isConfirmed,
      isInvoiced: input.status.isInvoiced,
      isQuotation: input.status.isQuotation,
      isVetted: input.status.isVetted,
      jobs: input.jobs.map((job) => {
        return {
          branchCode: input.branch.code,
          detailLine: job.description,
          jobCode: job.code,
          jobType: getEnumKeyByEnumValue(JobDetailType, job.type),
          repairOrderNumber: input.repairOrderNumber,
          isInvoiced: job.isInvoiced,
        } as IRepairOrderJob;
      }),
      owner: input.owner,
      pickUpDateTime: input.pickUpDateTime,
      progressStatus: input.progressStatus,
      registrationNumber: input.registrationNumber,
      repairOrderNumber: input.repairOrderNumber,
      vehicle: input.vehicle,
      fromDetail: true,
    } as IRepairOrder;
  }

  return undefined;
}

export function mapRepairOrderSummaryToRepairOrderWithJobs(input: IRepairOrderSummary) {
  if (input) {
    return {
      branch: input.branch,
      driver: input.driver,
      dropOffDateTime: input.dropOffDateTime,
      isClosed: input.status.isClosed,
      isConfirmed: input.status.isConfirmed,
      isInvoiced: input.status.isInvoiced,
      isQuotation: input.status.isQuotation,
      isVetted: input.status.isVetted,
      jobs: [] as IRepairOrderJob[],
      owner: input.owner,
      pickUpDateTime: input.pickUpDateTime,
      progressStatus: input.progressStatus,
      registrationNumber: input.registrationNumber,
      repairOrderNumber: input.repairOrderNumber,
      vehicle: input.vehicle,
      fromDetail: true,
    } as IRepairOrder;
  }

  return undefined;
}

export interface IRepairOrderSort {
  field: RepairOrderOrderByField;
  descending: boolean;
}

export enum RepairOrderOrderByField {
  dropOffDateTime,
}
